import type { ReactElement } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/shell/AppShell";
import { GuestShell } from "./components/shell/GuestShell";
import { isPublicCatalogPath } from "./lib/public-catalog";
import { authPath } from "./lib/return-path";
import { BrandSplash } from "./components/BrandSplash";
import { LoginPage, OAuthCallbackPage, RecoveryPage, RegisterPage, ResetPasswordPage } from "./pages/AuthPages";
import { EmailUnsubscribePage } from "./pages/EmailUnsubscribePage";
import { AssistantPage } from "./pages/AssistantPage";
import { CreateDreamPage } from "./pages/CreateDreamPage";
import { KycFilePage } from "./pages/kyc/KycFilePage";
import { KycProfilePage } from "./pages/kyc/KycProfilePage";
import { DiscoverPage } from "./pages/DiscoverPage";
import { HomePage } from "./pages/HomePage";
import { HubPage, ProductPage } from "./pages/HubPage";
import { SecurityPage } from "./pages/SecurityPage";
import { WalletLibrePage } from "./pages/WalletLibrePage";
import { TripPage } from "./pages/trips/TripPage";
import { SavedTripsPage } from "./pages/trips/SavedTripsPage";
import { PrivacyNoticePage } from "./pages/PrivacyNoticePage";
import { TermsPage } from "./pages/TermsPage";
import { CookiePolicyPage, LegalCenterPage } from "./pages/LegalCenterPage";
import { AdminAnalyticsPage } from "./pages/admin/AdminAnalyticsPage";
import { PersonalizedTripPage } from "./pages/trips/PersonalizedTripPage";
import { RequestsPage } from "./pages/requests/RequestsPage";
import { RequestReceivedPage } from "./pages/requests/RequestReceivedPage";
import { ApartadoCreatePage } from "./pages/apartados/ApartadoCreatePage";
import { ApartadoPage } from "./pages/apartados/ApartadoPage";
import { AdminTripPage, AdminTripsPage } from "./pages/admin/AdminTripsPage";
import { AdminReservationPage, AdminReservationsPage } from "./pages/admin/AdminReservationsPage";
import { AdminApartadoPage, AdminApartadosPage } from "./pages/admin/AdminApartadosPage";
import { AdminShell } from "./pages/admin/AdminShell";
import { StaffMfaGate } from "./components/StaffMfaGate";
import { AdminOverviewPage } from "./pages/admin/AdminOverviewPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminUserPage } from "./pages/admin/AdminUserPage";
import { AdminMediaPage } from "./pages/admin/AdminMediaPage";
import { AdminCatalogPage } from "./pages/admin/AdminCatalogPage";
import { AdminProductsPage } from "./pages/admin/AdminProductsPage";
import { AdminSettingsPage } from "./pages/admin/AdminSettingsPage";
import { AdminAuditPage } from "./pages/admin/AdminAuditPage";
import { AdminKuspitOverviewPage } from "./pages/admin/kuspit/AdminKuspitOverviewPage";
import { AdminKuspitContractsPage } from "./pages/admin/kuspit/AdminKuspitContractsPage";
import { AdminKuspitWebhooksPage } from "./pages/admin/kuspit/AdminKuspitWebhooksPage";
import { AdminKuspitCallsPage } from "./pages/admin/kuspit/AdminKuspitCallsPage";
import { AdminFundPage, AdminFundWalletPage } from "./pages/admin/fondos/AdminFundPage";
import { AdminLendingOverviewPage } from "./pages/admin/lending/AdminLendingOverviewPage";
import { AdminLendingReviewersPage } from "./pages/admin/lending/AdminLendingReviewersPage";
import { AdminLendingSettingsPage } from "./pages/admin/lending/AdminLendingSettingsPage";
import { LENDING_BASE, LendingTabs } from "./pages/admin/lending/LendingShared";
import { LoanApplicationPage } from "./pages/loan/LoanApplicationPage";
import { LoanEntryPage } from "./pages/loan/LoanEntryPage";
import { LoanStatusPage } from "./pages/loan/LoanStatusPage";
import { PartnerAccountPage } from "./pages/partner/PartnerAccountPage";
import { PartnerApplicationPage } from "./pages/partner/PartnerApplicationPage";
import { PartnerApplicationsPage } from "./pages/partner/PartnerApplicationsPage";
import { PartnerLoginPage } from "./pages/partner/PartnerLoginPage";
import { PartnerOverviewPage } from "./pages/partner/PartnerOverviewPage";
import { PARTNER_BASE, PartnerShell } from "./pages/partner/PartnerShell";

function ProtectedShell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  if (loading) return <BrandSplash />;
  if (!user && isPublicCatalogPath(location.pathname)) return <GuestShell />;
  if (!user) return <Navigate to={authPath("/login", returnTo)} replace state={{ from: returnTo }} />;
  // The lending partner's reviewers only ever see their portal.
  if (user.role === "lender") return <Navigate to={PARTNER_BASE} replace />;
  return <AppShell />;
}

/** The partner portal: Peso a Peso's reviewers, plus administrators who want to see what they see. */
function PartnerProtectedShell() {
  const { user, loading } = useAuth();
  if (loading) return <BrandSplash />;
  if (!user) return <Navigate to={`${PARTNER_BASE}/acceso`} replace />;
  if (user.role !== "lender" && user.role !== "admin") return <Navigate to={`${PARTNER_BASE}/acceso`} replace />;
  return <StaffMfaGate kicker="Portal de aliados · Peso a Peso"><PartnerShell /></StaffMfaGate>;
}

function LegacyLoanRedirect() {
  const { id } = useParams();
  return <Navigate to={`${LENDING_BASE}/solicitudes/${id}`} replace />;
}

function LoanStatusRoute() {
  const { id } = useParams();
  return <LoanStatusPage id={id} />;
}

/** Administration is a role and has its own shell: everyone else is sent back to their home. */
function AdminProtectedShell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  if (loading) return <BrandSplash />;
  if (!user) return <Navigate to={authPath("/login", returnTo)} replace state={{ from: returnTo }} />;
  if (user.role !== "admin") return <Navigate to="/inicio" replace />;
  return <StaffMfaGate kicker="Administración Aventuria"><AdminShell /></StaffMfaGate>;
}

/** The Peso a Peso queue pages are the partner's own screens, shown under the panel's tabs. */
function LendingQueue({ children }: { children: ReactElement }) {
  return <><LendingTabs />{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registro" element={<RegisterPage />} />
      <Route path="/recuperar" element={<RecoveryPage />} />
      <Route path="/restablecer" element={<ResetPasswordPage />} />
      <Route path="/correo/baja" element={<EmailUnsubscribePage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      <Route path="/aviso-de-privacidad" element={<PrivacyNoticePage />} />
      <Route path="/terminos-y-condiciones" element={<TermsPage />} />
      <Route path="/politica-de-cookies" element={<CookiePolicyPage />} />
      <Route path="/legal" element={<LegalCenterPage />} />
      <Route element={<ProtectedShell />}>
        <Route path="/inicio" element={<HomePage />} />
        <Route path="/explorar" element={<DiscoverPage />} />
        <Route path="/explorar/:slug" element={<DiscoverPage />} />
        <Route path="/viajes/a-la-medida" element={<PersonalizedTripPage />} />
        <Route path="/viajes/:slug" element={<TripPage />} />
        <Route path="/guardados" element={<SavedTripsPage />} />
        <Route path="/solicitudes" element={<RequestsPage />} />
        <Route path="/solicitudes/:id" element={<RequestReceivedPage />} />
        <Route path="/solicitudes/:id/apartado" element={<ApartadoCreatePage />} />
        <Route path="/apartados/:id" element={<ApartadoPage />} />
        <Route path="/asistente" element={<AssistantPage />} />
        <Route path="/wallet" element={<HubPage />} />
        <Route path="/wallet/libre" element={<WalletLibrePage />} />
        <Route path="/wallet/libre/nueva" element={<CreateDreamPage />} />
        <Route path="/wallet/:productId" element={<ProductPage />} />
        <Route path="/credito" element={<LoanEntryPage />} />
        <Route path="/credito/solicitud" element={<LoanApplicationPage />} />
        <Route path="/credito/solicitudes/:id" element={<LoanStatusRoute />} />
        <Route path="/perfil" element={<SecurityPage />} />
        <Route path="/verificacion/datos" element={<KycProfilePage />} />
        <Route path="/verificacion/expediente" element={<KycFilePage />} />
        <Route path="/seguridad" element={<Navigate to="/perfil" replace />} />
      </Route>
      <Route element={<AdminProtectedShell />}>
        <Route path="/admin" element={<AdminOverviewPage />} />
        <Route path="/admin/usuarios" element={<AdminUsersPage />} />
        <Route path="/admin/usuarios/:id" element={<AdminUserPage />} />
        <Route path="/admin/kuspit" element={<AdminKuspitOverviewPage />} />
        <Route path="/admin/kuspit/contratos" element={<AdminKuspitContractsPage />} />
        <Route path="/admin/kuspit/webhooks" element={<AdminKuspitWebhooksPage />} />
        <Route path="/admin/kuspit/llamadas" element={<AdminKuspitCallsPage />} />
        <Route path="/admin/fondos" element={<AdminFundPage />} />
        <Route path="/admin/fondos/:id" element={<AdminFundWalletPage />} />
        <Route path={LENDING_BASE} element={<AdminLendingOverviewPage />} />
        <Route path={`${LENDING_BASE}/solicitudes`} element={<LendingQueue><PartnerApplicationsPage base={LENDING_BASE} /></LendingQueue>} />
        <Route path={`${LENDING_BASE}/solicitudes/:id`} element={<LendingQueue><PartnerApplicationPage base={LENDING_BASE} /></LendingQueue>} />
        <Route path={`${LENDING_BASE}/aliados`} element={<AdminLendingReviewersPage />} />
        <Route path={`${LENDING_BASE}/configuracion`} element={<AdminLendingSettingsPage />} />
        <Route path="/admin/viajes" element={<AdminTripsPage />} />
        <Route path="/admin/viajes/:id" element={<AdminTripPage />} />
        <Route path="/admin/solicitudes" element={<AdminReservationsPage />} />
        <Route path="/admin/solicitudes/:id" element={<AdminReservationPage />} />
        <Route path="/admin/apartados" element={<AdminApartadosPage />} />
        <Route path="/admin/apartados/:id" element={<AdminApartadoPage />} />
        <Route path="/admin/fotos" element={<AdminMediaPage />} />
        <Route path="/admin/destinos" element={<AdminCatalogPage />} />
        <Route path="/admin/productos" element={<AdminProductsPage />} />
        <Route path="/admin/bitacora" element={<AdminAuditPage />} />
        <Route path="/admin/eventos" element={<AdminAnalyticsPage />} />
        <Route path="/admin/ajustes" element={<AdminSettingsPage />} />
        {/* Addresses from the previous layout keep working. */}
        <Route path="/admin/creditos" element={<Navigate to={`${LENDING_BASE}/solicitudes`} replace />} />
        <Route path="/admin/creditos/solicitudes" element={<Navigate to={`${LENDING_BASE}/solicitudes`} replace />} />
        <Route path="/admin/creditos/solicitudes/:id" element={<LegacyLoanRedirect />} />
        <Route path="/admin/aliados" element={<Navigate to={`${LENDING_BASE}/aliados`} replace />} />
      </Route>
      <Route path={`${PARTNER_BASE}/acceso`} element={<PartnerLoginPage />} />
      <Route element={<PartnerProtectedShell />}>
        <Route path={PARTNER_BASE} element={<PartnerOverviewPage base={PARTNER_BASE} />} />
        <Route path={`${PARTNER_BASE}/solicitudes`} element={<PartnerApplicationsPage base={PARTNER_BASE} />} />
        <Route path={`${PARTNER_BASE}/solicitudes/:id`} element={<PartnerApplicationPage base={PARTNER_BASE} />} />
        <Route path={`${PARTNER_BASE}/cuenta`} element={<PartnerAccountPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/inicio" replace />} />
    </Routes>
  );
}
